import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f7fa' },
  card: { background: '#fff', borderRadius: 12, padding: 40, width: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  sub: { color: '#666', marginBottom: 28, fontSize: 14 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6 },
  input: (hasError) => ({
    width: '100%', padding: '10px 14px', fontSize: 14, marginBottom: 4,
    border: `1px solid ${hasError ? '#ef4444' : '#ddd'}`, borderRadius: 6,
    boxSizing: 'border-box', outline: 'none',
  }),
  errorText: { color: '#ef4444', fontSize: 12, marginBottom: 12, display: 'block' },
  errorBox: {
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
    padding: '10px 14px', marginBottom: 16, color: '#dc2626', fontSize: 13,
  },
  btn: { width: '100%', padding: '12px', background: '#7c83fd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8 },
  btnDisabled: { opacity: 0.7, cursor: 'not-allowed' },
  link: { color: '#7c83fd', textDecoration: 'none', fontWeight: 600 },
  footer: { textAlign: 'center', fontSize: 13, color: '#666', marginTop: 20 },
};

export default function LoginPage() {
  const [form, setForm] = useState({ email: '', password: '', tenantCode: '' });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = location.state?.message || '';

  const validate = () => {
    const e = {};
    if (!form.email) e.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 8) e.password = 'Password must be at least 8 characters';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await login(form.email, form.password, form.tenantCode || undefined);
      navigate('/dashboard');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Login failed. Check your credentials.';
      setServerError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
    if (serverError) setServerError('');
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔐</div>
        <h1 style={s.title}>SecureOffer</h1>
        <p style={s.sub}>Sign in to your account</p>

        {successMessage && <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#166534', fontSize: 13 }}>✅ {successMessage}</div>}
        {serverError && <div style={s.errorBox}>⚠️ {serverError}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <label style={s.label}>Email</label>
          <input
            style={s.input(!!errors.email)}
            type="email"
            placeholder="you@company.com"
            value={form.email}
            onChange={handleChange('email')}
            autoComplete="email"
          />
          {errors.email && <span style={s.errorText}>{errors.email}</span>}

          <label style={s.label}>Password</label>
          <input
            style={s.input(!!errors.password)}
            type="password"
            placeholder="Your password"
            value={form.password}
            onChange={handleChange('password')}
            autoComplete="current-password"
          />
          {errors.password && <span style={s.errorText}>{errors.password}</span>}

          <label style={s.label}>Organisation <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></label>
          <input
            style={s.input(false)}
            type="text"
            placeholder="e.g. clickpay, infosys"
            value={form.tenantCode}
            onChange={handleChange('tenantCode')}
          />

          <button
            style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
            type="submit"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <Link to="/forgot-password" style={{ ...s.link, fontSize: 13, fontWeight: 400 }}>Forgot password?</Link>
          </div>
        </form>

        <div style={s.footer}>
          New organisation? <Link to="/register" style={s.link}>Create account</Link>
        </div>
      </div>
    </div>
  );
}
